'use client'

import * as React from 'react'
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle, RotateCcw, Loader2 } from 'lucide-react'
import { 
  borderRadiusV3, 
  spacingV3, 
  shadowsV3, 
  animationsV3, 
  glassV3,
  componentSpacing,
  componentAnimations,
  componentShadows,
  componentGlass,
  typographyV3,
  componentTypography
} from '@birhaus/tokens/v3'
import { cn, ClassValue } from '@birhaus/types/utils'

/**
 * BIRHAUS Toast V3 - Radical Minimalist Notifications
 * 
 * Revolutionary toast notification system with:
 * - Glass morphism with backdrop blur
 * - Radical border radius (24px default)
 * - Smooth slide animations
 * - Generous spacing (2x traditional)
 * - Enhanced undo patterns
 * - Spanish-first messaging
 * - Miller's Law compliance (max 3 toasts)
 * - Performance optimized
 */

// Using imported cn utility from @birhaus/types/utils

export type ToastVariantV3 = 'success' | 'error' | 'warning' | 'info' | 'loading'

export interface BirhausToastV3Data {
  id: string
  variant: ToastVariantV3
  title?: string
  titleEs?: string
  titleEn?: string
  message?: string
  messageEs?: string
  messageEn?: string
  duration?: number
  persistent?: boolean
  
  // V3 Enhanced features
  glassMorphism?: boolean
  glow?: boolean
  float?: boolean
  radius?: keyof typeof borderRadiusV3
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
  
  // Undo functionality
  undoAction?: () => void | Promise<void>
  undoLabel?: string
  undoLabelEs?: string
  undoLabelEn?: string
  undoTimeout?: number
  showUndoProgress?: boolean
  
  // Actions
  action?: {
    label: string
    labelEs?: string
    labelEn?: string
    onClick: () => void
    variant?: 'primary' | 'secondary' | 'glass'
  }
  
  onDismiss?: (id: string) => void
  locale?: 'es' | 'en'
  
  // Performance tracking
  trackPerformance?: boolean
}

interface ToastContextV3Type {
  toasts: BirhausToastV3Data[]
  addToast: (toast: Omit<BirhausToastV3Data, 'id'>) => string
  updateToast: (id: string, updates: Partial<BirhausToastV3Data>) => void
  dismissToast: (id: string) => void
  clearAllToasts: () => void
  locale: 'es' | 'en'
  setLocale: (locale: 'es' | 'en') => void
  maxToasts: number
  setMaxToasts: (max: number) => void
}

const ToastContextV3 = React.createContext<ToastContextV3Type | null>(null)

export interface BirhausToastV3ProviderProps {
  children: React.ReactNode
  locale?: 'es' | 'en'
  maxToasts?: number
  position?: BirhausToastV3Data['position']
  glassMorphism?: boolean
  className?: string
}

// BIRHAUS: Toast Provider with Miller's Law compliance
export const BirhausToastV3Provider: React.FC<BirhausToastV3ProviderProps> = ({
  children,
  locale = 'es',
  maxToasts = 3, // BIRHAUS: Miller's Law - max 3 toasts
  position = 'top-right',
  glassMorphism = true,
  className,
}) => {
  const [toasts, setToasts] = React.useState<BirhausToastV3Data[]>([])
  const [currentLocale, setCurrentLocale] = React.useState<'es' | 'en'>(locale)
  const [currentMaxToasts, setCurrentMaxToasts] = React.useState(maxToasts)
  
  const addToast = React.useCallback((toast: Omit<BirhausToastV3Data, 'id'>) => {
    const id = crypto.randomUUID()
    const newToast: BirhausToastV3Data = {
      id,
      position,
      glassMorphism,
      locale: currentLocale,
      ...toast,
    }
    
    setToasts(current => {
      // BIRHAUS: Enforce Miller's Law by removing oldest toasts
      const updatedToasts = [...current, newToast]
      if (updatedToasts.length > currentMaxToasts) {
        const excess = updatedToasts.length - currentMaxToasts
        return updatedToasts.slice(excess)
      }
      return updatedToasts
    })
    
    return id
  }, [position, glassMorphism, currentLocale, currentMaxToasts])
  
  const updateToast = React.useCallback((id: string, updates: Partial<BirhausToastV3Data>) => {
    setToasts(current => 
      current.map(toast => 
        toast.id === id ? { ...toast, ...updates } : toast
      )
    )
  }, [])
  
  const dismissToast = React.useCallback((id: string) => {
    setToasts(current => current.filter(toast => toast.id !== id))
  }, [])
  
  const clearAllToasts = React.useCallback(() => {
    setToasts([])
  }, [])
  
  const contextValue: ToastContextV3Type = {
    toasts,
    addToast,
    updateToast,
    dismissToast,
    clearAllToasts,
    locale: currentLocale,
    setLocale: setCurrentLocale,
    maxToasts: currentMaxToasts,
    setMaxToasts: setCurrentMaxToasts,
  }
  
  // Position classes for toast container
  const positionClasses = {
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6',
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'top-center': 'top-6 left-1/2 transform -translate-x-1/2',
    'bottom-center': 'bottom-6 left-1/2 transform -translate-x-1/2',
  }
  
  return (
    <ToastContextV3.Provider value={contextValue}>
      {children}
      
      {/* Toast Container */}
      <div
        className={cn(
          'fixed z-[100] flex flex-col gap-4 pointer-events-none',
          'max-w-sm w-full',
          positionClasses[position],
          className
        )}
        aria-live="polite"
        aria-label="Notificaciones"
      >
        {toasts.map(toast => (
          <BirhausToastV3Item
            key={toast.id}
            toast={toast}
            onDismiss={dismissToast}
          />
        ))}
      </div>
    </ToastContextV3.Provider>
  )
}

// Individual toast component
interface BirhausToastV3ItemProps {
  toast: BirhausToastV3Data
  onDismiss: (id: string) => void
}

const BirhausToastV3Item: React.FC<BirhausToastV3ItemProps> = ({
  toast,
  onDismiss,
}) => {
  const [isVisible, setIsVisible] = React.useState(false)
  const [isPaused, setIsPaused] = React.useState(false)
  const [timeRemaining, setTimeRemaining] = React.useState(toast.duration || 5000)
  const [isUndoing, setIsUndoing] = React.useState(false)
  const [undoTimeRemaining, setUndoTimeRemaining] = React.useState(0)
  
  const timeoutRef = React.useRef<NodeJS.Timeout>()
  const intervalRef = React.useRef<NodeJS.Timeout>()
  const undoTimeoutRef = React.useRef<NodeJS.Timeout>()
  const undoIntervalRef = React.useRef<NodeJS.Timeout>()
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    const locale = toast.locale || 'es'
    return locale === 'es' ? (spanish || english || fallback || '') : (english || spanish || fallback || '')
  }
  
  const displayTitle = resolveText(toast.titleEs, toast.titleEn, toast.title)
  const displayMessage = resolveText(toast.messageEs, toast.messageEn, toast.message)
  const displayUndoLabel = resolveText(
    toast.undoLabelEs,
    toast.undoLabelEn,
    toast.undoLabel || 'Deshacer'
  )
  const displayActionLabel = toast.action ? resolveText(
    toast.action.labelEs,
    toast.action.labelEn,
    toast.action.label
  ) : ''
  
  // Variant configurations
  const variantConfig = {
    success: {
      icon: CheckCircle2,
      color: 'green',
      bgClass: 'bg-green-50',
      borderClass: 'border-green-200',
      iconClass: 'text-green-600',
      titleClass: 'text-green-900',
      messageClass: 'text-green-700',
    },
    error: {
      icon: AlertCircle,
      color: 'red',
      bgClass: 'bg-red-50',
      borderClass: 'border-red-200',
      iconClass: 'text-red-600',
      titleClass: 'text-red-900',
      messageClass: 'text-red-700',
    },
    warning: {
      icon: AlertTriangle,
      color: 'yellow',
      bgClass: 'bg-yellow-50',
      borderClass: 'border-yellow-200',
      iconClass: 'text-yellow-600',
      titleClass: 'text-yellow-900',
      messageClass: 'text-yellow-700',
    },
    info: {
      icon: Info,
      color: 'blue',
      bgClass: 'bg-blue-50',
      borderClass: 'border-blue-200',
      iconClass: 'text-blue-600',
      titleClass: 'text-blue-900',
      messageClass: 'text-blue-700',
    },
    loading: {
      icon: Loader2,
      color: 'gray',
      bgClass: 'bg-gray-50',
      borderClass: 'border-gray-200',
      iconClass: 'text-gray-600',
      titleClass: 'text-gray-900',
      messageClass: 'text-gray-700',
    },
  }
  
  const config = variantConfig[toast.variant]
  const IconComponent = config.icon
  
  // Handle dismiss
  const handleDismiss = React.useCallback(() => {
    setIsVisible(false)
    setTimeout(() => {
      onDismiss(toast.id)
      toast.onDismiss?.(toast.id)
    }, 300) // Wait for exit animation
  }, [onDismiss, toast.id, toast.onDismiss])
  
  // Handle undo action
  const handleUndo = React.useCallback(async () => {
    if (toast.undoAction) {
      setIsUndoing(true)
      try {
        await toast.undoAction()
        handleDismiss()
      } catch (error) {
        console.error('Error executing undo action:', error)
      } finally {
        setIsUndoing(false)
      }
    }
  }, [toast.undoAction, handleDismiss])
  
  // Auto-dismiss timer
  React.useEffect(() => {
    if (toast.persistent) return
    
    setIsVisible(true)
    
    const startTimer = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      
      timeoutRef.current = setTimeout(() => {
        handleDismiss()
      }, timeRemaining)
    }
    
    const startCountdown = () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 100
          if (newTime <= 0) {
            clearInterval(intervalRef.current!)
            return 0
          }
          return newTime
        })
      }, 100)
    }
    
    if (!isPaused) {
      startTimer()
      startCountdown()
    }
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [toast.persistent, timeRemaining, isPaused, handleDismiss])
  
  // Undo timeout
  React.useEffect(() => {
    if (!toast.undoAction || !toast.undoTimeout) return
    
    const timeout = toast.undoTimeout
    setUndoTimeRemaining(timeout / 1000)
    
    if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current)
    if (undoIntervalRef.current) clearInterval(undoIntervalRef.current)
    
    // Countdown for undo
    undoIntervalRef.current = setInterval(() => {
      setUndoTimeRemaining(prev => {
        const newTime = prev - 0.1
        if (newTime <= 0) {
          clearInterval(undoIntervalRef.current!)
          return 0
        }
        return newTime
      })
    }, 100)
    
    // Auto-hide undo after timeout
    undoTimeoutRef.current = setTimeout(() => {
      setUndoTimeRemaining(0)
    }, timeout)
    
    return () => {
      if (undoTimeoutRef.current) clearTimeout(undoTimeoutRef.current)
      if (undoIntervalRef.current) clearInterval(undoIntervalRef.current)
    }
  }, [toast.undoAction, toast.undoTimeout])
  
  // Entry animation trigger
  React.useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50)
    return () => clearTimeout(timer)
  }, [])
  
  const radius = toast.radius || 'lg'
  const progressPercentage = toast.persistent ? 0 : ((timeRemaining / (toast.duration || 5000)) * 100)
  
  return (
    <div
      className={cn(
        'relative pointer-events-auto',
        'transform-gpu will-change-transform',
        'transition-transform duration-300 ease-in-out',
        isVisible ? 'animate-slide-in-right' : 'animate-slide-out-right',
        toast.float && 'hover:scale-105'
      )}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      role="alert"
      aria-live="assertive"
    >
      <div
        className={cn(
          // Base styles
          'relative overflow-hidden',
          'p-6 min-w-0',
          
          // Border radius - radical v3
          `rounded-[${borderRadiusV3[radius]}]`,
          
          // Glass morphism or solid background
          toast.glassMorphism ? [
            'bg-white/50 backdrop-blur-lg saturate-[150%] border border-white/30 shadow-xl',
            'backdrop-blur-lg',
            'border border-white/30',
          ] : [
            config.bgClass,
            config.borderClass,
            'border',
          ],
          
          // Shadow
          'shadow-lg hover:shadow-xl',
          
          // Glow effect
          toast.glow && `shadow-[0_0_30px_-8px_rgb(${config.color === 'green' ? '34,197,94' : config.color === 'red' ? '239,68,68' : config.color === 'yellow' ? '245,158,11' : '59,130,246'}/0.25)]`,
          
          // Performance optimizations
          'backface-visibility-hidden',
        )}
      >
        {/* Progress bar */}
        {!toast.persistent && (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200/50">
            <div
              className={cn(
                'h-full transition-all duration-100 ease-linear',
                `bg-${config.color}-500`
              )}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        )}
        
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="flex-shrink-0 mt-0.5">
            <IconComponent 
              size={24} 
              className={cn(
                config.iconClass,
                toast.variant === 'loading' && 'animate-spin'
              )}
            />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            {displayTitle && (
              <h4 className={cn(
                'text-sm font-semibold mb-1',
                config.titleClass
              )}>
                {displayTitle}
              </h4>
            )}
            
            {displayMessage && (
              <p className={cn(
                'text-sm leading-relaxed',
                config.messageClass
              )}>
                {displayMessage}
              </p>
            )}
            
            {/* Actions */}
            {(toast.action || (toast.undoAction && undoTimeRemaining > 0)) && (
              <div className="flex items-center gap-3 mt-4">
                {/* Undo action */}
                {toast.undoAction && undoTimeRemaining > 0 && (
                  <button
                    onClick={handleUndo}
                    disabled={isUndoing}
                    className={cn(
                      'inline-flex items-center gap-2',
                      'px-4 py-2 rounded-xl',
                      'text-sm font-medium',
                      'bg-white/80 hover:bg-white',
                      'border border-gray-200',
                      'transition-all duration-200',
                      'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
                      'disabled:opacity-50 disabled:cursor-not-allowed'
                    )}
                  >
                    {isUndoing ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <RotateCcw size={16} />
                    )}
                    <span>{displayUndoLabel}</span>
                    {toast.showUndoProgress && undoTimeRemaining > 0 && (
                      <span className="text-xs opacity-75">
                        ({Math.ceil(undoTimeRemaining)}s)
                      </span>
                    )}
                  </button>
                )}
                
                {/* Custom action */}
                {toast.action && (
                  <button
                    onClick={toast.action.onClick}
                    className={cn(
                      'inline-flex items-center gap-2',
                      'px-4 py-2 rounded-xl',
                      'text-sm font-medium',
                      'transition-all duration-200',
                      'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
                      
                      // Variant styles
                      toast.action.variant === 'primary' && [
                        'bg-blue-600 text-white',
                        'hover:bg-blue-700',
                      ],
                      toast.action.variant === 'glass' && [
                        'bg-white/30 backdrop-blur-md saturate-[150%] border border-white/20 shadow-lg',
                        'text-gray-700 hover:bg-white/70',
                      ],
                      (!toast.action.variant || toast.action.variant === 'secondary') && [
                        'bg-white/80 hover:bg-white',
                        'border border-gray-200',
                        'text-gray-700',
                      ]
                    )}
                  >
                    {displayActionLabel}
                  </button>
                )}
              </div>
            )}
          </div>
          
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className={cn(
              'flex-shrink-0 p-1 rounded-lg',
              'text-gray-400 hover:text-gray-600',
              'hover:bg-white/50',
              'transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-gray-500/20'
            )}
            aria-label="Cerrar notificación"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  )
}

// Hook to use the toast context
export const useBirhausToastV3 = () => {
  const context = React.useContext(ToastContextV3)
  
  if (!context) {
    throw new Error('useBirhausToastV3 must be used within a BirhausToastV3Provider')
  }
  
  return context
}

// Standalone toast component for individual use
export interface BirhausToastV3StandaloneProps extends Omit<BirhausToastV3Data, 'id'> {
  onClose?: () => void
  className?: string
}

export const BirhausToastV3Standalone: React.FC<BirhausToastV3StandaloneProps> = ({
  onClose,
  className,
  ...toastProps
}) => {
  const toast: BirhausToastV3Data = {
    id: 'standalone',
    ...toastProps,
  }
  
  return (
    <div className={cn('max-w-sm', className)}>
      <BirhausToastV3Item
        toast={toast}
        onDismiss={onClose || (() => {})}
      />
    </div>
  )
}

// Default toast messages in Spanish-first
export const birhausToastV3Messages = {
  success: {
    titleEs: 'Éxito',
    titleEn: 'Success',
    saved: {
      messageEs: 'Guardado exitosamente',
      messageEn: 'Saved successfully'
    },
    deleted: {
      messageEs: 'Eliminado exitosamente',
      messageEn: 'Deleted successfully'
    },
    updated: {
      messageEs: 'Actualizado exitosamente',
      messageEn: 'Updated successfully'
    }
  },
  error: {
    titleEs: 'Error',
    titleEn: 'Error',
    generic: {
      messageEs: 'Algo salió mal',
      messageEn: 'Something went wrong'
    },
    network: {
      messageEs: 'Error de conexión',
      messageEn: 'Connection error'
    }
  },
  info: {
    titleEs: 'Información',
    titleEn: 'Information'
  },
  warning: {
    titleEs: 'Advertencia',
    titleEn: 'Warning'
  }
} as const